import { useEffect, useState, useCallback } from 'react'
import { ClipLoader } from 'react-spinners'
import './MyKeysView.css'
import { ReactComponent as CopyIcon } from './copy-icon.svg'
import { ReactComponent as CheckIcon } from './check-icon.svg'
import { ReactComponent as SettingsIcon } from './settings-icon.svg'
import * as AuthToken from '../../AuthToken'
import { getEditoryCustomizeViewURL, getUserInfo, getDevProfile, updateDevProfile, getOrCreateTryUserSession,
  genSecreteAPIKey, getSecreteAPIKey, getAvalableTaskTokens } from '../../backendAPI'
import * as doc  from '../../docUrls'

// Base URLs for the "try session" buttons; the session auth_token is appended as a query param
const IFRAME_SESSION_URL = 'https://freevideoeditor.online/html/test_iframe.html'
const REACT_COMPONENT_URL = 'https://smarteditingonline.solveigmm.com/react_video_editor_example'
const API_DEV_HOST = 'https://smarteditingonline.solveigmm.com'


export const MyKeysView = () => {
    const [error, setError] = useState(null)
    const [loading, setLoading] = useState(true)
    const [apiKey, setApiKey] = useState('')
    const [taskTokensCount, setTaskTokensCount] = useState('')
    const [secreteAPIKey, setSecreteApiKey] = useState('')
    const [callbackInput, setCallbackInput] = useState('')
    const [callbackSaved, setCallbackSaved] = useState(false)
    const [fetchFileUrlInput, setFetchFileUrlInput] = useState('')
    const [fetchFileUrlSaved, setFetchFileUrlSaved] = useState(false)
    const [mediaTabContentUrlInput, setMediaTabContentUrlInput] = useState('')
    const [mediaTabContentUrlSaved, setMediaTabContentUrlSaved] = useState(false)
    const [fetchThumbnailUrlInput, setFetchThumbnailUrlInput] = useState('')
    const [fetchThumbnailUrlSaved, setFetchThumbnailUrlSaved] = useState(false)
    const [advancedOpen, setAdvancedOpen] = useState(false)
    const [copiedField, setCopiedField] = useState(null)

    const copyToClipboard = useCallback(async (text, field) => {
        try {
            await navigator.clipboard.writeText(text)
            setCopiedField(field)

            setTimeout(() => {
                setCopiedField(null)
            }, 2000)
        } catch { }
    }, [])

    const openCustomizeView = useCallback(() => {
        const token = AuthToken.get()
        window.open(getEditoryCustomizeViewURL(token), '_blank')
    }, [])

    const openTrySession = useCallback(async (url) => {
        try {
            const sessionId = await getOrCreateTryUserSession(apiKey, secreteAPIKey)
            window.open(`${url}?auth_token=${sessionId}`, '_blank')
        } catch {
            alert("Failed to create user session!")
        }
    }, [apiKey, secreteAPIKey])

    const openUserSession = useCallback(() => {
        openTrySession(`${API_DEV_HOST}/projects/new`)
    }, [openTrySession])

    const openIframeSession = useCallback(() => {
        openTrySession(IFRAME_SESSION_URL)
    }, [openTrySession])

    const openReactComponentSession = useCallback(() => {
        openTrySession(REACT_COMPONENT_URL)
    }, [openTrySession])

    const saveField = useCallback(async (field, value, setSaved) => {
        if (!apiKey) return
        try {
            const response = await updateDevProfile(apiKey, { [field]: value })
            if (response.ok) {
                setSaved(true)
                setTimeout(() => setSaved(false), 2000)
            }
        } catch {}
    }, [apiKey])


    const fetchDevProfile = useCallback(async () => {
        const token = AuthToken.get()
        if (!token) return
        try {
            setLoading(true)
            const {userId} = await getUserInfo(token)
            const apiKey = userId

            const devProfile = await getDevProfile(apiKey)
            let curSecreteAPIKey = await getSecreteAPIKey(token)
            if (!curSecreteAPIKey) {
              curSecreteAPIKey = await genSecreteAPIKey(token)
            }
            const curTaskTokensCount =  await getAvalableTaskTokens(token, curSecreteAPIKey)
            setSecreteApiKey(curSecreteAPIKey)
            setApiKey(apiKey)
            setTaskTokensCount(curTaskTokensCount)
            setCallbackInput(devProfile.callbackUrl || '')
            setFetchFileUrlInput(devProfile.fetchFileUrl || '')
            setMediaTabContentUrlInput(devProfile.mediaTabContentUrl || '')
            setFetchThumbnailUrlInput(devProfile.fetchThumbnailUrl || '')
        } catch (err) {
            setError(err.message)
        } finally {
           setLoading(false)
        }
    }, [])

    useEffect(() => {
        fetchDevProfile()
    }, [fetchDevProfile])

    return (
        <div className="my-keys-container">
            {loading && <div className="loader-container"><ClipLoader color="#3498db" size={200} /> </div>}
            {error && (
                <div className="error-message">
                    <div className="error-message-bg">
                        <pre className="error-wrap-text">{error}</pre>
                    </div>
                </div>
            )}
            {!error && !loading && (
               <div className='content-page-container'>
                    <div className='page-title'>
                        Your API Key
                    </div>
                    <div className='page-content-text'>
                        Used to customize and connect your application to SolveigMM Video Editor.<br/>
                        See <a className='link' href={doc.customization} target='_blank' rel='noopener noreferrer'>Customization</a> for more details
                    </div>

                    <div className='api-key-wrapper'>
                        <div className='api-key'>
                            {apiKey}
                        </div>
                        <button
                                className='copy-btn'
                                onClick={() => copyToClipboard(apiKey, 'apiKey')}
                                title='Copy API key'
                            >
                                {copiedField === 'apiKey'
                                    ? <CheckIcon />
                                    : <CopyIcon />}
                        </button>
                    </div>
                    <br/>
                    <div className='page-title'>
                        Your Secret API Key
                    </div>
                    <div className='page-content-text'>
                        Authorizes access and allows you to extend usage limits.
                        Keep this key on your backend and never expose it to the client.<br/>
                        See <a className='link' href={doc.createUserWithSecreteApiKey} target='_blank' rel='noopener noreferrer'>Create a user with a Secret API Key</a> for more details
                    </div>

                    <div className='api-key-wrapper'>
                        <div className='api-key'>
                            {secreteAPIKey}
                        </div>
                        <button
                            className='copy-btn'
                            onClick={() => copyToClipboard(secreteAPIKey, 'secretApiKey')}
                            title='Copy Secret API key'
                        >
                            {copiedField === 'secretApiKey'
                                ? <CheckIcon />
                                : <CopyIcon />}
                        </button>
                    </div>

                   <div className={`balance ${Number(taskTokensCount) === 0 ? 'zero-balance' : ''}`}>
                      Remaining task tokens: {taskTokensCount}
                   </div>

                    <div className='action-row'>
                        <button className='customize-btn' onClick={openCustomizeView}>
                            <SettingsIcon />
                            Customize the Video Editor UI
                        </button>
                        <button className='try-session-btn' onClick={openUserSession}>
                            Try a user session
                        </button>
                        <button className='try-session-btn' onClick={openIframeSession}>
                            Try an iframe session
                        </button>
                        <button className='try-session-btn' onClick={openReactComponentSession}>
                            Try the React component
                        </button>
                    </div>
                    <div className='section-divider' />

                    <div className='advanced-settings-expander'>
                        <div className='advanced-settings-header' onClick={() => setAdvancedOpen(!advancedOpen)}>
                            <span className='advanced-settings-title'>Advanced Settings</span>
                            <svg
                                className={`advanced-settings-chevron ${advancedOpen ? 'open' : ''}`}
                                width='16' height='16' viewBox='0 0 16 16' fill='none'
                                xmlns='http://www.w3.org/2000/svg'
                            >
                                <path d='M4 6l4 4 4-4' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'/>
                            </svg>
                        </div>

                        <div className={`advanced-settings-body ${advancedOpen ? 'open' : 'closed'}`}>
                                <div className='page-title'>
                                    Callback URL
                                </div>
                                <div className='page-content-text'>
                                    Used to receive events triggered by the user while using the editor (project_create, project_update, render_start, render_complete).<br/>
                                    See <a className='link' href={doc.callBackEvents} target='_blank' rel='noopener noreferrer'>Callback events</a> for details
                                </div>

                                <div className='callback-url-wrapper'>
                                    <input
                                        className='callback-url-input'
                                        type='text'
                                        value={callbackInput}
                                        onChange={e => setCallbackInput(e.target.value)}
                                        placeholder='https://example.com/callback'
                                    />
                                    <button className='set-btn' onClick={() => saveField('callbackUrl', callbackInput, setCallbackSaved)}>
                                        {callbackSaved ? 'Saved' : 'Set'}
                                    </button>
                                </div>

                                <div className='section-divider' />
                                <div className='page-title'>
                                     Custom URLs
                                </div>
                                <div className='page-content-text'>
                                    Used by the editor to fetch information about media assets displayed in the Media tab.<br/>
                                    See <a className='link' href={doc.overrideMediaAssets} target='_blank' rel='noopener noreferrer'> Override Media Assets Information API</a> and
                                    <a className='link' href={doc.generateMediaInfo} target='_blank' rel='noopener noreferrer'> Generate Media Info </a> for details
                                </div>

                                <div className='custom-url-field'>
                                    <div className='custom-url-label'>fetchFileUrl</div>
                                    <div className='callback-url-wrapper'>
                                        <input className='callback-url-input' type='text' value={fetchFileUrlInput} onChange={e => setFetchFileUrlInput(e.target.value)} placeholder='https://example.com/api/mediafiles/&lt;id&gt;' />
                                        <button className='set-btn' onClick={() => saveField('fetchFileUrl', fetchFileUrlInput, setFetchFileUrlSaved)}>{fetchFileUrlSaved ? 'Saved' : 'Set'}</button>
                                    </div>
                                </div>

                                <div className='custom-url-field'>
                                    <div className='custom-url-label'>mediaTabContentUrl</div>
                                    <div className='callback-url-wrapper'>
                                        <input className='callback-url-input' type='text' value={mediaTabContentUrlInput} onChange={e => setMediaTabContentUrlInput(e.target.value)} placeholder='https://example.com/api/mediafiles' />
                                        <button className='set-btn' onClick={() => saveField('mediaTabContentUrl', mediaTabContentUrlInput, setMediaTabContentUrlSaved)}>{mediaTabContentUrlSaved ? 'Saved' : 'Set'}</button>
                                    </div>
                                </div>

                                <div className='custom-url-field'>
                                    <div className='custom-url-label'>fetchThumbnailUrl</div>
                                    <div className='callback-url-wrapper'>
                                        <input className='callback-url-input' type='text' value={fetchThumbnailUrlInput} onChange={e => setFetchThumbnailUrlInput(e.target.value)} placeholder='https://example.com/api/clip_thumbnails' />
                                        <button className='set-btn' onClick={() => saveField('fetchThumbnailUrl', fetchThumbnailUrlInput, setFetchThumbnailUrlSaved)}>{fetchThumbnailUrlSaved ? 'Saved' : 'Set'}</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

            )}
        </div>
    )
}
